import firebase from 'firebase/app'
import 'firebase/auth'
import { AuthProvider } from './user'

export const getAuthProvider = (
  providerData: (firebase.UserInfo | null)[] | undefined,
): AuthProvider => {
  let provider: AuthProvider
  if (providerData && providerData.length && providerData[0]) {
    const providerId = providerData[0].providerId
    if (providerId !== 'google.com' && providerId !== 'password') {
      throw new Error(`Invalid auth provider: ${providerId}, data: ${JSON.stringify(providerData)}`)
    } else {
      provider = providerId
    }
  } else {
    provider = 'password'
  }
  return provider
}

type CurrentUserInfo = {
  loggedIn: boolean
  emailVerified: boolean
  userId: string
  displayName: string
  provider: AuthProvider
  email: string
  photoURL: string
}

/**
 * Used to get the user auth info object.
 *
 * @returns current user info
 */
export const getCurrentUserInfo = (): CurrentUserInfo => {
  const { currentUser } = firebase.auth()
  const providerData = currentUser?.providerData
  return {
    loggedIn: Boolean(currentUser),
    emailVerified: currentUser ? currentUser.emailVerified : false,
    userId: currentUser ? currentUser.uid || '' : '',
    displayName: currentUser ? currentUser.displayName || '' : '',
    provider: getAuthProvider(providerData),
    email: currentUser?.email || '',
    photoURL: currentUser?.photoURL || '',
  }
}

/**
 * A simple helper to check whether user is logged-in.
 * Used primarly to determine whether public or authorized endpoints should be used to get config.
 *
 * @returns a boolean indicating user log-in status
 */
export const isLoggedIn = (): boolean => {
  const { currentUser } = firebase.auth()
  return !!currentUser
}

/**
 * Used to ensure user is logged in, error out otherwise.
 *
 * @returns returns user auth data, or throws if not logged in.
 */
export const ensureCurrentUser = (): firebase.User => {
  const { currentUser } = firebase.auth()
  if (!currentUser) {
    throw Error('Logged out.')
  }
  return currentUser
}

/**
 * Used to get the user's ID token, which in turn is used to make authorized requests.
 *
 * @returns the firebase auth user token used for endpooints hits.
 */
export function getCurrentUserIdToken(): Promise<string> {
  const currentUser = ensureCurrentUser()
  const currentUserIdToken = currentUser.getIdToken()
  return currentUserIdToken
}
/**
 * Get current user uid, needed to f.e. add user uid to a request
 */
export function getCurrentUserUid(): string {
  const currentUser = ensureCurrentUser()
  return currentUser.uid
}

// Thin wrappers to decouple firebase from user module in store
// Making sure user module is agnostic to auth provider
export const createUserWithEmailAndPassword = async (email: string, password: string) => {
  const userCredential = await firebase.auth().createUserWithEmailAndPassword(email, password)
  const user = userCredential.user
  return user
}
export const signInWithEmailAndPassword = async (email: string, password: string) => {
  await firebase.auth().signInWithEmailAndPassword(email, password)
}

export const googleLogIn = async (isSignUpAllowed: boolean = false) => {
  const provider = new firebase.auth.GoogleAuthProvider()
  const signInWithPopup = await firebase.auth().signInWithPopup(provider)
  const isNewUser = signInWithPopup.additionalUserInfo?.isNewUser
  // We cannot prevent Google login from acting as signup and creating new users,
  // but we can attempt to delete them right away, if needed (f.e. config does not allow signups)
  if (!isSignUpAllowed) {
    if (isNewUser) {
      const currentUser = firebase.auth().currentUser
      try {
        await currentUser?.delete()
      } catch (err) {
        throw Error('User delete error.')
      }
      throw Error(
        'Because of high demand for Renderro services creating new accounts is temporary blocked.',
      )
    }
  }
}

export const signOut = async () => {
  await firebase.auth().signOut()
}

export const reauthenticate = async (currentPassword: string) => {
  const user = firebase.auth().currentUser
  if (!user || !user.email) {
    return
  }
  const cred = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword)
  await user.reauthenticateWithCredential(cred)
}

export const sendPasswordResetEmail = async (email: string) => {
  await firebase.auth().sendPasswordResetEmail(email)
}
